// frontend/src/shared/hooks/async/useAsyncState.ts

/**
 * ✅ Hook genérico para manejar estados async (loading/error)
 *
 * Elimina duplicación entre:
 * - useCompanyData.ts
 * - useCompanyMetrics.ts
 * - useExport.ts
 * - useDeleteRespuesta.ts
 * - useCopyResponses.ts
 * - useLogin.ts
 *
 * Reducción estimada: ~200 líneas duplicadas
 *
 * Este hook centraliza el patrón común de:
 * 1. useState(loading) y useState(error)
 * 2. setLoading(true) + setError(null) al inicio
 * 3. try-catch-finally con setLoading(false)
 * 4. setError en catch
 */

import { useState, useCallback } from 'react';

// ========== INTERFACES ==========

export interface UseAsyncStateOptions {
	/** Si debe capturar y almacenar errores (default: true) */
	captureErrors?: boolean;
	/** Si debe resetear error antes de cada operación (default: true) */
	resetErrorOnStart?: boolean;
}

export interface UseAsyncStateReturn {
	/** Si está cargando actualmente */
	loading: boolean;
	/** Mensaje de error si lo hay */
	error: string | null;
	/** Función para ejecutar operaciones async con manejo automático */
	execute: <T>(operation: () => Promise<T>) => Promise<T | null>;
	/** Función para envolver funciones async (alternativa a execute) */
	wrapAsync: <TArgs extends any[], TResult>(
		fn: (...args: TArgs) => Promise<TResult>
	) => (...args: TArgs) => Promise<TResult | null>;
	/** Función para establecer error manualmente */
	setError: (error: string | null) => void;
	/** Función para limpiar el error */
	clearError: () => void;
	/** Función para establecer loading manualmente */
	setLoading: (loading: boolean) => void;
}

// ========== HOOK PRINCIPAL ==========

/**
 * Hook para manejar estados de operaciones asíncronas
 *
 * @example
 * // Uso con execute (para una sola operación)
 * const { loading, error, execute } = useAsyncState();
 *
 * const deleteRespuesta = async (id: ID) => {
 *   return execute(async () => {
 *     const result = await RespuestaService.deleteRespuesta(id);
 *     return result;
 *   });
 * };
 *
 * @example
 * // Uso con wrapAsync (para múltiples operaciones)
 * const { loading, error, wrapAsync } = useAsyncState();
 *
 * const exportSingle = wrapAsync(async (empresa, anyo, ...) => {
 *   // lógica aquí
 *   return result;
 * });
 *
 * const exportAnnual = wrapAsync(async (empresa, anyo, ...) => {
 *   // lógica aquí
 *   return result;
 * });
 *
 * @example
 * // Uso sin captura de errores (para hooks que manejan errores custom)
 * const { loading, error, execute } = useAsyncState({ captureErrors: false });
 *
 * const operation = async () => {
 *   try {
 *     return await execute(async () => {
 *       // operación
 *     });
 *   } catch {
 *     // manejo custom del error
 *   }
 * };
 */
export function useAsyncState(
	options: UseAsyncStateOptions = {}
): UseAsyncStateReturn {
	const {
		captureErrors = true,
		resetErrorOnStart = true,
	} = options;

	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	/**
	 * Ejecuta una operación async con manejo automático de loading/error
	 */
	const execute = useCallback(
		async <T,>(operation: () => Promise<T>): Promise<T | null> => {
			setLoading(true);
			if (resetErrorOnStart) {
				setError(null);
			}

			try {
				const result = await operation();
				return result;
			} catch (err) {
				if (captureErrors) {
					const errorMessage =
						err instanceof Error ? err.message : 'Unknown error';
					setError(errorMessage);
				}
				// Siempre lanzar el error para que el hook que llama pueda manejarlo
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[captureErrors, resetErrorOnStart]
	);

	/**
	 * Envuelve una función async para que maneje loading/error automáticamente
	 * Útil cuando tienes múltiples funciones en el mismo hook
	 */
	const wrapAsync = useCallback(
		<TArgs extends any[], TResult>(
			fn: (...args: TArgs) => Promise<TResult>
		): ((...args: TArgs) => Promise<TResult | null>) => {
			return async (...args: TArgs): Promise<TResult | null> => {
				return execute(() => fn(...args));
			};
		},
		[execute]
	);

	/**
	 * Limpia el error
	 */
	const clearError = useCallback(() => {
		setError(null);
	}, []);

	return {
		loading,
		error,
		execute,
		wrapAsync,
		setError,
		clearError,
		setLoading,
	};
}

export default useAsyncState;
